import React from 'react';
import { logEvent } from '../lib/logEvent.js';

const MAX_ERRORS = 100;

// Route slug allowlist mirrors the app_events CHECK constraint.
// Derive a best-effort slug from window.location without reading any PII.
function currentRouteSlug() {
  if (typeof window === 'undefined') return 'unknown';
  const path = window.location.pathname || '';
  const hash = window.location.hash || '';
  const combined = (path + hash).toLowerCase();
  if (combined.includes('admin'))           return 'admin-dashboard';
  if (combined.includes('raid'))            return 'raid-intake';
  if (combined.includes('n400-workshop'))   return 'n400-workshop';
  if (combined.includes('i589-workshop'))   return 'i589-workshop';
  if (combined.includes('i589-builder'))    return 'i589-builder';
  if (combined.includes('n400-builder'))    return 'n400-builder';
  if (combined.includes('civics'))          return 'civics';
  if (combined.includes('eligibility'))     return 'eligibility';
  if (combined.includes('combo-coa'))       return 'combo-coa';
  if (combined.includes('ar11') || combined.includes('ar-11')) return 'ar11';
  if (combined.includes('eoir33') || combined.includes('eoir-33')) return 'eoir33';
  if (combined.includes('motion-venue') || combined.includes('change-venue')) return 'motion-venue';
  if (combined.includes('case-status'))     return 'case-status';
  if (combined.includes('eoir-lookup') || combined.includes('hearing-lookup')) return 'eoir-lookup';
  if (combined.includes('detainee'))        return 'detainee-locator';
  if (combined.includes('org-picker'))      return 'org-picker';
  if (combined === '/' || combined === '')  return 'home';
  return 'unknown';
}

function recordError(error, info) {
  if (typeof window === 'undefined') return;
  if (!Array.isArray(window.__NOVO_ERRORS__)) {
    window.__NOVO_ERRORS__ = [];
  }
  window.__NOVO_ERRORS__.push({
    message: String(error?.message || error),
    stack: error?.stack || null,
    componentStack: info?.componentStack || null,
    timestamp: Date.now(),
  });
  if (window.__NOVO_ERRORS__.length > MAX_ERRORS) {
    window.__NOVO_ERRORS__.shift();
  }
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('Novo Navigator crashed:', error, info);
    recordError(error, info);
    // Persistent diagnostic log — no PII: no message, stack, or componentStack.
    logEvent({
      kind: 'client_error',
      error_name: (error?.name || error?.constructor?.name || 'Error').slice(0, 64),
      route: currentRouteSlug(),
    });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          padding: '32px',
          maxWidth: '640px',
          margin: '64px auto',
          fontFamily: '-apple-system, Segoe UI, Roboto, sans-serif',
          color: '#08314d',
        }}>
          <h1 style={{ marginTop: 0 }}>Something went wrong.</h1>
          <p>Novo Navigator hit an unexpected error. Try refreshing the page. If the problem keeps happening, contact your administrator.</p>
          <details style={{ marginTop: 16, fontSize: 13, color: '#555' }}>
            <summary>Technical details</summary>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}
